import { cn } from "@u/cn";
import { dashboardSectionLabelKey } from "@u/dashboardSections";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

export interface DashboardBreadcrumb {
  label: string;
  to?: string;
}

export interface DashboardHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: DashboardBreadcrumb[];
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  /** Inline next to the title (e.g. the per-view help/guide icons) — kept out
   *  of the action row so utility controls don't outrank the primary CTA. */
  titleAside?: ReactNode;
  /** Rendered inline right after the title text (e.g. a count badge). */
  titleBadge?: ReactNode;
  className?: string;
}

export function DashboardHeader({
  title,
  description,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  titleAside,
  titleBadge,
  className,
}: DashboardHeaderProps) {
  const hasActions = Boolean(primaryAction || secondaryActions);
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Prepend the sidebar section (Clientes / Organización / Configuración del
  // sistema) as a crumb between "home" and the view — General routes return
  // null and stay as-is. Non-linked: sections have no landing page.
  const sectionKey = dashboardSectionLabelKey(pathname);
  const crumbs =
    breadcrumbs && breadcrumbs.length > 0 && sectionKey
      ? [breadcrumbs[0], { label: t(sectionKey) }, ...breadcrumbs.slice(1)]
      : breadcrumbs;

  return (
    <header
      className={cn(
        "flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6 mb-5 md:mb-6",
        className,
      )}
    >
      <div className="space-y-3 w-full md:w-auto min-w-0">
        {crumbs && crumbs.length > 0 && (
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 label-micro text-muted-foreground">
              {crumbs.map((crumb, i) => (
                <Fragment key={`${crumb.label}-${i}`}>
                  {i > 0 && <li aria-hidden>/</li>}
                  <li>
                    {crumb.to ? (
                      <Link
                        to={crumb.to}
                        className="hover:text-foreground transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-foreground">{crumb.label}</span>
                    )}
                  </li>
                </Fragment>
              ))}
            </ol>
          </nav>
        )}

        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-4xl sm:text-5xl md:text-6xl leading-[0.95] wrap-break-word text-balance">
            {title}
          </h1>
          {titleBadge}
          {/* Guide/manual ride the title row on mobile (desktop keeps them in
              the action cluster, right-aligned). */}
          {titleAside && (
            <span className="md:hidden ml-auto flex items-center gap-2 self-center">
              {titleAside}
            </span>
          )}
        </div>

        {description && <p className="text-body max-w-xl">{description}</p>}
      </div>

      {(hasActions || titleAside) && (
        <div
          className={cn(
            "w-full md:w-auto md:flex flex-col md:flex-row md:items-center justify-start md:justify-end gap-3 animate-in fade-in duration-500",
            // Mobile: only takes a row when there are real actions; a lone
            // guide/manual already rides the title row above.
            hasActions ? "flex" : "hidden",
          )}
        >
          {/* Guide/manual utility buttons sit at the row's end; when action
              buttons exist they go to their left. Desktop only — on mobile they
              ride the title row (above). */}
          {titleAside && (
            <span className="hidden md:flex items-center gap-2 shrink-0">
              {titleAside}
            </span>
          )}
          {hasActions && (
            <div className="flex flex-col md:flex-row md:items-center gap-3 *:w-full md:*:w-auto [&_button]:w-full md:[&_button]:w-auto">
              {/* ponytail: stretch direct children + their buttons full-width
                  below md; covers the common 1-2 action case (Export + CTA) */}
              {secondaryActions}
              {primaryAction}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
